import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useAdyen, useI18n } from '~Hooks';
import { v4 as uuid } from 'uuid';
import styled from '@emotion/styled';

import { useStorage } from '~/Components/Storage';
import NumberInput from '~ComponentsImproved/Numpad';
import LoginModal from '~Scenes/Tables/Components/LoginModal';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';

import { CreateExpenseMutation, CreateExpenseMutationVariables } from './__queries__';
import { useReportsApi } from '~Components/ReportsUniverse';

const Wrapper = styled.div`
  width: 100vw;
  height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 49;
  animation: fade-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const InnerWrapper = styled.div<{
  top?: number;
}>`
  position: absolute;
  top: 200px;
  width: 100%;
  text-align: center;
  font-size: 35px;
  color: #fff;
  font-weight: bold;
  transition: all 200ms ease-out;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const ErrorMessage = styled.p`
  text-align: center;
  width: 50%;
  margin: 0 auto;
`;

const ExitWrapper = styled.div<{
  top?: number;
}>`
  position: absolute;
  top: 200px;
  width: 100%;
  text-align: center;
  font-size: 35px;
  color: #fff;
  font-weight: bold;
  transition: all 200ms ease-out;
  animation: fade-and-translate-in 200ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const Subtitle = styled.div`
  font-size: 25px;
  color: #fff;
  font-weight: 300;
`;

const ConfirmButton = styled.div`
  padding: 10px 15px;
  margin: 20px auto;
  color: #333;
  background: #fafafa;
  border-radius: 2px;
  width: 300px;
`;

const CloseButton = styled.div`
  padding: 10px 15px;
  margin: 20px auto;
  color: #fff;
  background: #951e1f;
  border-radius: 2px;
  width: 48px;
  z-index: 1;
`;

interface Props {
  venueId: string;
  onComplete: () => void;
  onFail: () => void;
}

const CREATE_EXPENSE = gql`
  mutation CreateExpenseMutation($companyId: ID!, $staffId: ID!, $venueId: ID!, $amount: Int!) {
    createExpense(
      data: {
        company: { connect: { id: $companyId } }
        venue: { connect: { id: $venueId } }
        staff: { connect: { id: $staffId } }
        paymentType: "ADYEN"
        amount: $amount
      }
    ) {
      id
    }
  }
`;

const adyenKey = process.env.ADYEN_KEY!;
const adyenPass = process.env.ADYEN_PASS!;

const securityKey = {
  adyenCryptoVersion: 1,
  keyIdentifier: adyenKey,
  keyVersion: 0,
  passphrase: adyenPass,
};

const ExpensesFlow: React.FunctionComponent<Props> = ({ venueId, onComplete, onFail }) => {
  const [storage] = useStorage();
  const { i18n } = useI18n();
  const adyen = useAdyen();
  const reportsApi = useReportsApi();
  const companyId = useSelector((state) => state.company);
  const staffId = useSelector((state) => state.staff?.id);
  const staffs = useSelector((state) => state.companyStaff?.staffs ?? []);
  const [amount, setAmount] = useState<number | null>(null);
  const [error, setError] = useState<null | string>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [selectedTerminal, setSelectedTerminal] = useState<
    typeof storage.adyenTerminals[0] | null
  >(null);

  const [createExpense, { loading }] = useMutation<
    CreateExpenseMutation,
    CreateExpenseMutationVariables
  >(CREATE_EXPENSE);

  function handleCompleted() {
    setIsCompleted(true);

    setTimeout(() => {
      onComplete();
    }, 1000);
  }

  const handleSuccess = (amount: number, staffId: string) => {
    createExpense({
      variables: {
        amount,
        companyId,
        staffId,
        venueId,
      },
    }).then(({ data }) => {
      if (data?.createExpense.id) {
        reportsApi.refundOrder('', [], [{ amount, name: 'ADYEN' }]);
        reportsApi.addExpense(amount, 'ADYEN');
        handleCompleted();
      } else {
        setError(i18n('Adyen.Expense.Error.Generic'));
      }
    });
  };

  useEffect(() => {
    if (!amount || !selectedTerminal || !staffId) {
      return;
    }

    const id = Math.round(Math.random() * 100000000).toString();
    adyen.refundUnreferenced(selectedTerminal.POIID, id, amount).then((response) => {
      if (!response?.success) {
        setError(i18n('Adyen.Expense.Error.LostConnection'));
        return;
      }

      handleSuccess(amount, staffId);
    });
  }, [amount, selectedTerminal]);

  return (
    <CloseButton
      onTouchStart={(e) => {
        onFail();
      }}>
      {i18n('Adyen.Close')}
    </CloseButton>
  );

  return (
    <Wrapper className="background">
      {(() => {
        if (isCompleted) {
          return <ExitWrapper>{i18n('Adyen.Expense.ReportAdded')}</ExitWrapper>;
        }

        if (!staffId) {
          return <LoginModal />;
        }

        if (error) {
          return (
            <InnerWrapper>
              <ErrorMessage>{i18n('Adyen.Expense.Error.LostConnection')}</ErrorMessage>
              <ConfirmButton onClick={() => handleSuccess(amount!, staffId!)}>Yes</ConfirmButton>
              <ConfirmButton onClick={() => onFail()}>No</ConfirmButton>
            </InnerWrapper>
          );
        }

        if (loading) {
          return (
            <InnerWrapper>
              {i18n('Adyen.Loading')}
              <Subtitle>{i18n('Adyen.PleaseWait')}</Subtitle>
            </InnerWrapper>
          );
        }

        if (!amount) {
          return (
            <NumberInput
              maxCount={5}
              decimal={true}
              onSubmit={(digits: string) => {
                const val = Math.round(parseFloat(digits) * 100);

                setAmount(val);
              }}
              onClose={() => {
                onFail();
              }}
            />
          );
        }

        if (selectedTerminal) {
          return (
            <InnerWrapper>
              {i18n('Adyen.InsertCard')}
              <Subtitle>{i18n('Adyen.Waiting')}</Subtitle>
            </InnerWrapper>
          );
        }

        return (
          <InnerWrapper>
            {i18n('Adyen.PickTerminal')}
            {storage.adyenTerminals.map((terminal) => (
              <ConfirmButton key={terminal.POIID} onClick={() => setSelectedTerminal(terminal)}>
                {terminal.POIID}
              </ConfirmButton>
            ))}
          </InnerWrapper>
        );
      })()}
      {!error && (
        <CloseButton
          onTouchStart={(e) => {
            if (!loading && !isCompleted) {
              onFail();
            }
          }}>
          {i18n('Adyen.Close')}
        </CloseButton>
      )}
    </Wrapper>
  );

  return null;
};

export default ExpensesFlow;
